import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {executeReport, reRunReport, SendToMeOptions} from '../../store/actions/reportResultActions';
import {
  getReportExecutionStatusSelector,
  getReportLastResultSelector
} from '../../store/reducers/reportResultReducer';
import {
  closeProgressToast,
  createProgressToast,
  updateProgressToastMessage,
} from './executeReportToast';
import usePrevious from '../../../shared/hooks/use-previous';
import {FINISH_STATUS} from './constants';

type OnFinishCallback = (lastResultId: string) => void;

export function useExecuteReport(reportId: string, name?: string): any {
  let progressKey = useRef<string | null>(null);
  let onFinishRef = useRef<OnFinishCallback | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const loadingStatus = useSelector(getReportExecutionStatusSelector(reportId));
  const lastResult = useSelector(getReportLastResultSelector(reportId));
  const previousResultId = usePrevious(lastResult?.uid);

  useEffect(() => {
    return () => {
      onFinishRef.current = null;
      if (progressKey.current) {
        closeProgressToast(progressKey.current);
      }
    }
  }, []);

  useEffect(() => {
    if (loading && loadingStatus && FINISH_STATUS.includes(loadingStatus) && !!lastResult) {
      setLoading(false);
    }
  }, [loading, loadingStatus, lastResult, previousResultId]);

  useEffect(() => {
    if (progressKey.current && previousResultId !== lastResult?.uid) {
      updateProgressToastMessage(progressKey.current as string, name, lastResult);

      if (onFinishRef.current) {
        onFinishRef.current(lastResult?.uid as string);
        onFinishRef.current = null;
      }
    }
  }, [previousResultId, lastResult?.uid]);

  const onExecute = (options?: SendToMeOptions) => {
    setLoading(true);
    dispatch(executeReport(reportId, options));

    progressKey.current = createProgressToast(name);
  };

  const onReRun = (
    flowRunId: string,
    taskRunIds: string[],
    parentTaskRunId: string,
    onFinish?: OnFinishCallback,
  ) => {
    setLoading(true);

    dispatch(reRunReport(reportId, flowRunId, taskRunIds, parentTaskRunId));

    progressKey.current = createProgressToast(name);
    onFinishRef.current = onFinish || null;
  };

  return {
    loading,
    onExecute,
    onReRun,
  };
}
